import type { DbEndorsementStatus } from '@domain/schemas/contract.schema';
import { DbEndorsementStatusSchema, ResourceState } from '@domain/schemas/contract.schema';

type EndorsementTransitionsByStatus = Record<DbEndorsementStatus, DbEndorsementStatus[]>;

const ENDORSEMENT_USER_TRANSITIONS_WITH_REVIEW: EndorsementTransitionsByStatus = {
  ARCHIVED: [ResourceState.DRAFT],
  DRAFT: [ResourceState.REVIEW, ResourceState.ARCHIVED],
  REVIEW: [ResourceState.FINAL, ResourceState.DRAFT, ResourceState.ARCHIVED],
  FINAL: [],
};

const ENDORSEMENT_ADMIN_TRANSITIONS_WITH_REVIEW: EndorsementTransitionsByStatus = {
  ARCHIVED: [ResourceState.DRAFT],
  DRAFT: [ResourceState.REVIEW, ResourceState.ARCHIVED],
  REVIEW: [ResourceState.FINAL, ResourceState.DRAFT, ResourceState.ARCHIVED],
  FINAL: [ResourceState.DRAFT, ResourceState.REVIEW],
};

// Legacy matrix used when the EndorsementReview feature flag is off — Draft transitions directly
// to Final without any Review step. REVIEW still has exits so an endorsement already parked there
// (flag flipped off after the fact, or entered via an API path that doesn't respect the flag) can
// still be moved out — mirrors how FIRM_ORDER keeps its exits when FirmOrderStatus is disabled.
const ENDORSEMENT_USER_TRANSITIONS_NO_REVIEW: EndorsementTransitionsByStatus = {
  ARCHIVED: [ResourceState.DRAFT],
  DRAFT: [ResourceState.FINAL, ResourceState.ARCHIVED],
  REVIEW: [ResourceState.DRAFT, ResourceState.FINAL, ResourceState.ARCHIVED],
  FINAL: [],
};

const ENDORSEMENT_ADMIN_TRANSITIONS_NO_REVIEW: EndorsementTransitionsByStatus = {
  ARCHIVED: [ResourceState.DRAFT],
  DRAFT: [ResourceState.FINAL, ResourceState.ARCHIVED],
  REVIEW: [ResourceState.DRAFT, ResourceState.FINAL, ResourceState.ARCHIVED],
  FINAL: [ResourceState.DRAFT],
};

const isEndorsementStatus = (status?: string): status is DbEndorsementStatus =>
  DbEndorsementStatusSchema.options.includes(status as DbEndorsementStatus);

type EndorsementTransitionArgs = {
  sourceStatus?: string;
  isAdmin?: boolean;
  isEndorsementReviewEnabled?: boolean;
};

export const getAllowedEndorsementTargetStatuses = ({
  sourceStatus,
  isAdmin,
  isEndorsementReviewEnabled = true,
}: EndorsementTransitionArgs): DbEndorsementStatus[] => {
  if (!isEndorsementStatus(sourceStatus)) {
    return [];
  }

  const transitions = isEndorsementReviewEnabled
    ? isAdmin
      ? ENDORSEMENT_ADMIN_TRANSITIONS_WITH_REVIEW
      : ENDORSEMENT_USER_TRANSITIONS_WITH_REVIEW
    : isAdmin
      ? ENDORSEMENT_ADMIN_TRANSITIONS_NO_REVIEW
      : ENDORSEMENT_USER_TRANSITIONS_NO_REVIEW;

  return transitions[sourceStatus];
};

export const canTransitionEndorsementStatus = ({
  sourceStatus,
  targetStatus,
  isAdmin,
  isEndorsementReviewEnabled = true,
}: EndorsementTransitionArgs & { targetStatus?: string }) => {
  if (!isEndorsementStatus(targetStatus)) {
    return false;
  }

  if (sourceStatus === targetStatus) {
    return true;
  }

  return getAllowedEndorsementTargetStatuses({ sourceStatus, isAdmin, isEndorsementReviewEnabled }).includes(
    targetStatus,
  );
};
